import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Section, StateView, useAuth } from "@ozpanel/ui";

// Node identity against the cluster CA — when each node's certificate expires, and whether the
// last renewal worked.
//
// The data has always been there: `node_certificates` records every certificate ever issued, a
// renewal writes a NEW row and leaves the old one at is_current=0 so the history is the audit
// trail, and GET /cluster/certificates already answers all of it including which certificates the
// peer channel accepts right now. NOTHING CALLED IT. Asked where to see this, the honest answer
// was "nowhere" — the certs module's Overview tab rendered an empty div.
//
// It matters because of what happens at the end of the window: the peer channel REFUSES an expired
// identity, so a node whose renewal has been quietly failing does not degrade, it drops out — and
// the symptoms surface as Galera losing a peer or a rollout failing to reach a node, never as
// "this certificate expired".

interface NodeCert {
  node_name?: string;
  node_uuid?: string;
  subject_cn?: string;
  fingerprint?: string;
  not_after?: string;
  is_current?: boolean;
  accepted_on_peer_channel?: boolean;
  revoked?: boolean;
  why?: string;
}

/** Days until a date, or null when it cannot be read — never 0, which would read as "today". */
function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86400000);
}

export function NodeIdentity() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-certificates"],
    queryFn: () =>
      client.apiFetch<{ certificates: NodeCert[] | null; accepted_now?: number }>("/cluster/certificates"),
    retry: false,
  });

  // The renewal loop's own state. A certificate that looks fine today and a renewal task that has
  // been failing for a week are the same picture until the day it expires, so both belong here.
  const tasks = useQuery({
    queryKey: ["schedule-tasks"],
    queryFn: () => client.apiFetch<{ tasks: Record<string, unknown>[] | null }>("/schedule"),
    retry: false,
  });
  const renewal = (tasks.data?.tasks ?? []).find((t) => String(t.task) === "cluster.identity");

  const current = (q.data?.certificates ?? []).filter((c) => c.is_current);
  const superseded = (q.data?.certificates ?? []).filter((c) => !c.is_current);

  const cols: DataListColumn<NodeCert>[] = [
    {
      key: "node", label: "Node", sortable: true, value: (c) => c.node_name ?? "",
      render: (c) => <span className="font-medium">{c.node_name || "unknown"}</span>,
    },
    {
      key: "expires", label: "Identity expires", sortable: true,
      value: (c) => daysUntil(c.not_after) ?? 99999,
      render: (c) => {
        const d = daysUntil(c.not_after);
        if (d === null) return <span className="text-muted">unknown</span>;
        // Renewal runs inside a 30-day window, so under 30 means the last attempt did not work —
        // that is the number worth colouring, not the raw date.
        const tone = d < 7 ? "bad" : d < 30 ? "warn" : "ok";
        return (
          <span className="flex items-center gap-2">
            <StatusPill tone={tone}>{d} days</StatusPill>
            <span className="text-[11.5px] text-muted">{String(c.not_after).slice(0, 10)}</span>
          </span>
        );
      },
    },
    {
      key: "accepted", label: "Peer channel", value: (c) => (c.accepted_on_peer_channel ? 1 : 0),
      render: (c) =>
        c.accepted_on_peer_channel ? (
          <StatusPill tone="ok">accepted</StatusPill>
        ) : (
          <StatusPill tone="bad">refused</StatusPill>
        ),
    },
    {
      key: "cn", label: "Identity", render: (c) => (
        <span className="font-mono text-[11.5px] text-muted">{(c.subject_cn || "").slice(0, 18)}…</span>
      ),
    },
  ];

  return (
    <>
      <Section title="Node identity" hint={q.data ? `${current.length} current` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
            Every node holds a certificate signed by the cluster CA, and the node-to-node channel
            refuses one that has expired. A node whose renewal has been failing does not slow
            down — it drops out, and it shows up as the database losing a peer or a rollout not
            reaching a node, never as an expired certificate.
          </p>
          <StateView loading={q.isLoading} error={q.error} errorContext="node identities">
            <DataList
              items={current}
              columns={cols}
              getRowKey={(c) => String(c.fingerprint)}
              emptyMessage="No current node identity recorded — the peer channel cannot authenticate anybody."
            />
          </StateView>
        </div>
      </Section>

      <Section title="Renewal" className="mt-6">
        <StateView loading={tasks.isLoading} error={tasks.error} errorContext="the renewal task">
          {renewal ? (
            <div className="space-y-1 text-[12.5px]">
              <div>
                Task <span className="font-mono">cluster.identity</span> ·{" "}
                {String(renewal.last_run ?? "") ? (
                  <>
                    last ran {String(renewal.last_run).slice(0, 19).replace("T", " ")} —{" "}
                    {renewal.last_ok === false ? (
                      <StatusPill tone="bad">it failed</StatusPill>
                    ) : (
                      <StatusPill tone="ok">succeeded</StatusPill>
                    )}
                  </>
                ) : (
                  // Never having run is NOT the same as having run and succeeded, and the
                  // difference decides whether anybody is watching this at all.
                  <StatusPill tone="warn">has not run since this node started</StatusPill>
                )}
              </div>
              <p className="max-w-3xl text-[11.5px] leading-relaxed text-muted">
                Runs hourly — not because a 30-day window needs it, but so a failure is noticed
                within the hour. A follower cannot mint its own identity: if renewal fails there,
                the task reports what an operator has to do rather than pretending it worked.
              </p>
            </div>
          ) : (
            <p className="text-[12.5px] text-muted">
              The renewal task is not registered on this node, so nothing is keeping its identity
              valid.
            </p>
          )}
        </StateView>
      </Section>

      {superseded.length > 0 && (
        <Section title="Superseded and revoked" className="mt-6" bodyClassName="p-0">
          <div className="p-4">
            <p className="mb-2 max-w-3xl text-[12px] leading-relaxed text-muted">
              Kept deliberately: a renewal writes a new certificate and leaves the old row in place,
              so revocation history can still be read against it. An evicted node&apos;s
              certificates are revoked here, which is what stops it rejoining.
            </p>
            <DataList
              items={superseded}
              columns={cols}
              getRowKey={(c) => String(c.fingerprint)}
              emptyMessage="None."
            />
          </div>
        </Section>
      )}
    </>
  );
}
